/**
 * When the reader last did anything in this tab, and whether a field they
 * entered something into is still on the page. A tab that reloads by itself
 * reads both, so it never reloads under someone using it, or over what they
 * typed.
 */

const DOCUMENT_ACTIVITY_EVENTS = [
  'pointerdown',
  'pointermove',
  'keydown',
  'wheel',
  'touchstart',
  'scroll',
  // A reader coming back to the tab, or the tab waking from a freeze
  'visibilitychange',
  'resume',
];
const WINDOW_ACTIVITY_EVENTS = ['focus', 'pageshow'];

// A field whose value has not been saved raises no flag of its own, so any entry counts
const ENTRY_EVENTS = ['input', 'change'];

let lastActiveAt = 0;
let watchers = 0;

/**
 * Fields entered into on this page. A field that has left the document — a
 * closed modal's, a click-to-edit field once committed — took its value with
 * it, so it does not count.
 */
const enteredFields = new Set<Node>();

const markActive = () => {
  lastActiveAt = Date.now();
};

const markEntered = (event: Event) => {
  if (event.target instanceof Node) {
    enteredFields.add(event.target);
  }
  markActive();
};

// The document's are captured, since a scroll inside a page's own container
// does not bubble. The window's are not: every element's focus passes through
// the window's capture phase, and a modal moving focus is not the reader.
const listen = (method: 'addEventListener' | 'removeEventListener') => {
  DOCUMENT_ACTIVITY_EVENTS.forEach(type => document[method](type, markActive, true));
  WINDOW_ACTIVITY_EVENTS.forEach(type => window[method](type, markActive));
  ENTRY_EVENTS.forEach(type => document[method](type, markEntered, true));
};

/**
 * Listens for as long as any caller is watching, counting the reader present
 * as it starts, since a tab is watched from when it loads. The returned stop is
 * safe to call twice.
 */
export const watchReaderActivity = () => {
  if (watchers === 0) {
    markActive();
    listen('addEventListener');
  }
  watchers += 1;

  let isStopped = false;
  return () => {
    if (isStopped) {
      return;
    }
    isStopped = true;
    watchers -= 1;
    if (watchers === 0) {
      listen('removeEventListener');
    }
  };
};

/**
 * How long the reader has been away. Input inside an embedded frame, such as a
 * video player, never reaches this document, so a frame holding focus counts
 * as in use.
 */
export const getIdleMs = () =>
  document.activeElement instanceof HTMLIFrameElement ? 0 : Date.now() - lastActiveAt;

export const hasEnteredOnPage = () => {
  enteredFields.forEach(field => {
    if (!field.isConnected) {
      enteredFields.delete(field);
    }
  });
  return enteredFields.size > 0;
};

/** For each change of page, whose fields start empty. */
export const startPage = () => {
  enteredFields.clear();
};
